import { useState } from 'react';
import { IconButton, Tooltip, colors } from '@sweep-io/sweep-design';
import { Copy, Check } from '@sweep-io/sweep-design/dist/icons';
import { telemetry } from '../../../../telemetry';

interface CopyButtonProps {
  messageRef: React.RefObject<HTMLDivElement>;
}

export const RichTextCopyButton = ({ messageRef }: CopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (!messageRef.current) {
        telemetry.captureError(new Error('Message element not found for copying'));
        return;
      }

      const htmlContent = messageRef.current.innerHTML;
      const textContent = messageRef.current.textContent || messageRef.current.innerText || '';
      const clipboardData = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textContent], { type: 'text/plain' }),
      });

      await navigator.clipboard.write([clipboardData]);
      setIsCopied(true);

      // Reset the icon after 2 seconds
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      telemetry.captureError(new Error('Failed to copy rich content'), error);
      // Fallback to plain text copy
      try {
        if (messageRef.current) {
          const textContent = messageRef.current.textContent || messageRef.current.innerText || '';
          await navigator.clipboard.writeText(textContent);
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        }
      } catch (fallbackError) {
        telemetry.captureError(new Error('Fallback copy also failed'), fallbackError);
      }
    }
  };

  return (
    <Tooltip title={isCopied ? 'Copied' : 'Copy'} placement="top">
      <span>
        <IconButton onClick={handleCopy} size="tiny" variant="flat">
          {isCopied ? <Check color={colors.grey[800]} /> : <Copy />}
        </IconButton>
      </span>
    </Tooltip>
  );
};
